// src/services/api.js

import axios from 'axios';

// The configuration (baseURL, interceptors) is now handled globally,
// so we can directly define the API methods.

const api = {

  // ============================
  // Building Endpoints (Stavby)
  // ============================

  /**
   * Fetches a list of all buildings.
   * @returns {Promise<AxiosResponse<Array>>}
   */
  getAllBuildings() {
    return axios.get('/buildings/');
  },

  /**
   * Fetches a single building by its database ID.
   * @param {number} buildingId
   * @returns {Promise<AxiosResponse<Object>>}
   */
  getBuilding(buildingId) {
    return axios.get(`/buildings/${buildingId}`);
  },

  /**
   * Creates a new building (corresponds to Step A1).
   * @param {Object} buildingData - (BuildingInSchema)
   * @returns {Promise<AxiosResponse<Object>>}
   */
  createBuilding(buildingData) {
    return axios.post('/buildings/', buildingData);
  },

  /**
   * Updates a building with a generic payload.
   * Ideal for updating Step A1 data or general edits.
   * @param {number} buildingId - The ID of the building to update.
   * @param {Object} buildingData - The data to update.
   * @returns {Promise<AxiosResponse<Object>>}
   */
  updateBuilding(buildingId, buildingData) {
    return axios.patch(`/buildings/${buildingId}/`, buildingData); // Předpokládáme obecný PATCH endpoint
  },

  /**
   * Deletes a building by its ID.
   * @param {number} buildingId
   * @returns {Promise<AxiosResponse<Object>>}
   */
  deleteBuilding(buildingId) {
    return axios.delete(`/buildings/${buildingId}`);
  },

  // --- Step-specific Building Updates ---

  /**
   * Updates a building's Step 2 data (Risk Analysis).
   * @param {number} buildingId
   * @param {Object} data - (BuildingStep2UpdateSchema)
   * @returns {Promise<AxiosResponse<Object>>}
   */
  updateBuildingStep2(buildingId, data) {
    return axios.patch(`/buildings/${buildingId}/step2`, data);
  },

  /**
   * Updates a building's Step 3 data (Minimum Standards).
   * @param {number} buildingId
   * @param {Object} data - (BuildingStep3UpdateSchema)
   * @returns {Promise<AxiosResponse<Object>>}
   */
  updateBuildingStep3(buildingId, data) {
    return axios.patch(`/buildings/${buildingId}/step3`, data);
  },

  /**
   * Updates a building's Step 4 data (Structural Assessment).
   * @param {number} buildingId
   * @param {Object} data - (BuildingStep4UpdateSchema)
   * @returns {Promise<AxiosResponse<Object>>}
   */
  updateBuildingStep4(buildingId, data) {
    return axios.patch(`/buildings/${buildingId}/step4`, data);
  },

  /**
   * Updates a building's Step 7 data (Review and Approval).
   * @param {number} buildingId
   * @param {Object} data - (BuildingStep7UpdateSchema)
   * @returns {Promise<AxiosResponse<Object>>}
   */
  updateBuildingStep7(buildingId, data) {
    return axios.patch(`/buildings/${buildingId}/step7`, data);
  },

  // ===================================
  // Shelter Space Endpoints (Úkryty)
  // ===================================

  /**
   * Creates a new shelter space within a building (corresponds to Step A3).
   * @param {Object} shelterData - (ShelterInSchema)
   * @returns {Promise<AxiosResponse<Object>>}
   */
  createShelter(shelterData) {
    return axios.post('/shelter_spaces/', shelterData);
  },
  
  /**
   * Deletes a shelter space by its ID.
   * @param {number} shelterId
   * @returns {Promise<AxiosResponse<Object>>}
   */
  deleteShelter(shelterId) {
    return axios.delete(`/shelter_spaces/${shelterId}`);
  },
  
  // --- Step-specific Shelter Updates ---
  
  /**
   * Updates a shelter's Step 3 data (Identification).
   * @param {number} shelterId
   * @param {Object} data - (ShelterInSchema)
   * @returns {Promise<AxiosResponse<Object>>}
   */
  updateShelterStep3(shelterId, data) {
    return axios.patch(`/shelter_spaces/${shelterId}/step3`, data);
  },

  /**
   * Updates a shelter's Step 4 data (Structural Score).
   * @param {number} shelterId
   * @param {Object} data - (ShelterStep4UpdateSchema)
   * @returns {Promise<AxiosResponse<Object>>}
   */
  updateShelterStep4(shelterId, data) {
    return axios.patch(`/shelter_spaces/${shelterId}/step4`, data);
  },

  /**
   * Updates a shelter's Step 5 data (Threat Assessment).
   * @param {number} shelterId
   * @param {Object} data - (ShelterStep5UpdateSchema)
   * @returns {Promise<AxiosResponse<Object>>}
   */
  updateShelterStep5(shelterId, data) {
    return axios.patch(`/shelter_spaces/${shelterId}/step5`, data);
  },

  /**
   * Updates a shelter's Step 6 data (Overall Assessment).
   * @param {number} shelterId
   * @param {Object} data - (ShelterStep6UpdateSchema)
   * @returns {Promise<AxiosResponse<Object>>}
   */
  updateShelterStep6(shelterId, data) {
    return axios.patch(`/shelter_spaces/${shelterId}/step6`, data);
  },

  // --- Photo Upload/Delete Functions ---
  /**
   * Uploads one or more photos for a specific shelter.
   * @param {number} shelterId The ID of the shelter.
   * @param {File[]} files An array of File objects from the input.
   * @returns {Promise<AxiosResponse<Object>>} The updated shelter object.
   */
  uploadShelterPhotos(shelterId, files) {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    return axios.post(`/shelter_spaces/${shelterId}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /**
   * Deletes a specific photo from a shelter.
   * @param {number} shelterId The ID of the shelter.
   * @param {string} filename The name of the file to delete.
   * @returns {Promise<AxiosResponse<Object>>} A status message.
   */
  deleteShelterPhoto(shelterId, filename) {
    return axios.delete(`/shelter_spaces/${shelterId}/photos/${encodeURIComponent(filename)}`);
  },

    /**
   * Uploads one or more schema files for a specific shelter.
   * @param {number} shelterId The ID of the shelter.
   * @param {File[]} files The File objects from the input.
   * @returns {Promise<AxiosResponse<Object>>} The updated shelter object.
   */
  uploadShelterSchemas(shelterId, files) {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return axios.post(`/shelter_spaces/${shelterId}/schemas`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /**
   * Deletes a specific schema file from a shelter.
   * @param {number} shelterId The ID of the shelter.
   * @param {string} filename The name of the file to delete.
   * @returns {Promise<AxiosResponse<Object>>} A status message.
   */
  deleteShelterSchema(shelterId, filename) {
    return axios.delete(`/shelter_spaces/${shelterId}/schemas/${encodeURIComponent(filename)}`);
  },

   uploadBuildingSsdAttachments(buildingId, files) {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return axios.post(`/buildings/${buildingId}/ssd_attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  deleteBuildingSsdAttachment(buildingId, filename) {
    return axios.delete(`/buildings/${buildingId}/ssd_attachments/${encodeURIComponent(filename)}`);
  },
  
  uploadBuildingSisAttachments(buildingId, files) {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return axios.post(`/buildings/${buildingId}/sis_attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  deleteBuildingSisAttachment(buildingId, filename) {
    return axios.delete(`/buildings/${buildingId}/sis_attachments/${encodeURIComponent(filename)}`);
  },

  minDistance(lat, lon) {
    return axios.get(`/min_distance/${lat}/${lon}`);
  },

  /**
   * Fetches a summary list of all buildings, each including the highest
   * s_c score from its associated shelters.
   * @returns {Promise<AxiosResponse<Array>>}
   */
  getBuildingsSummary() {
    return axios.get('/buildings/summary');
  },
};

export default api;